import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import Header from '@/components/Header'
import { getUsers, createUser, updateUser, deleteUser, getAllMerchants, getMeMerchants } from '@/services/api'

export default function Users() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [filters, setFilters] = useState<{ email: string; role: '' | 'admin' | 'operator' | 'auditor'; page: number; limit: number }>({ email: '', role: '', page: 1, limit: 10 })
  const [list, setList] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [creating, setCreating] = useState<{ email: string; password: string; role: 'admin' | 'operator' | 'auditor'; companyName: string; merchant_id?: string }>({ email: '', password: '', role: 'operator', companyName: '', merchant_id: undefined })
  const [editId, setEditId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<{ email: string; password: string; role: 'admin' | 'operator' | 'auditor'; companyName: string; merchant_id?: string }>({ email: '', password: '', role: 'operator', companyName: '', merchant_id: undefined })
  const pageCount = Math.ceil(total / filters.limit) || 1
  
  const [manageMerchantId, setManageMerchantId] = useState<string>('')
  const [allMerchants, setAllMerchants] = useState<Array<{ id: string; name: string }>>([])

  useEffect(() => { load() }, [filters, user?.activeMerchantId, manageMerchantId])

  useEffect(() => {
    const fetchAll = async () => {
      try {
        if (user?.role === 'system_admin') {
          const r = await getAllMerchants({ page: 1, limit: 100 })
          const list = Array.isArray(r.data?.merchants) ? r.data.merchants : []
          setAllMerchants(list.map((m: any) => ({ id: String(m.id), name: String(m.name) })))
          if (!manageMerchantId && list[0]) setManageMerchantId(String(list[0].id))
        } else if (user?.role === 'admin') {
          const r = await getMeMerchants()
          const list = Array.isArray(r.data?.merchants) ? r.data.merchants : []
          const opts = list.map((m: any) => ({ id: String(m.id), name: String(m.name) }))
          setAllMerchants(opts)
          const current = String(user?.activeMerchantId || '')
          if (!manageMerchantId) setManageMerchantId(current || (opts[0]?.id || ''))
        }
      } catch {}
    }
    fetchAll()
  }, [user?.role])

  const load = async () => {
    try {
      const r = await getUsers({
        email: filters.email || undefined,
        role: filters.role || undefined,
        page: filters.page,
        limit: filters.limit,
        merchant_id: user?.role === 'system_admin' ? (manageMerchantId || undefined) : (user?.activeMerchantId || undefined)
      })
      setList(r.data?.users ?? [])
      setTotal(r.data?.pagination?.total ?? 0)
    } catch {}
  }

  const saveNew = async () => {
    if (!creating.email || !creating.password || !creating.companyName) return
    try {
      const payload = { ...creating }
      if (user?.role === 'system_admin') {
        payload.merchant_id = manageMerchantId || undefined
      } else if (user?.role === 'admin') {
        payload.merchant_id = (manageMerchantId as any) || (user.activeMerchantId as any) || undefined
      }
      await createUser(payload)
      setCreating({ email: '', password: '', role: 'operator', companyName: '' })
      await load()
      alert('已创建用户')
    } catch {
      alert('创建失败')
    }
  }

  const saveEdit = async (id: string) => {
    try {
      const payload: any = { email: editForm.email, role: editForm.role, companyName: editForm.companyName }
      if (editForm.password) payload.password = editForm.password
      if (user && user.role === 'system_admin') payload.merchant_id = editForm.merchant_id || undefined
      await updateUser(id, payload)
      setEditId(null)
      await load()
      alert('已保存')
    } catch {
      alert('保存失败')
    }
  }

  const remove = async (id: string) => {
    try {
      await deleteUser(id)
      await load()
      alert('已删除')
    } catch {
      alert('删除失败')
    }
  }

  if (!user || (user.role !== 'admin' && user.role !== 'system_admin')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white shadow rounded p-6">
          <div className="mb-4">权限不足</div>
          <button className="px-3 py-2 border rounded" onClick={() => navigate('/dashboard')}>返回控制台</button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="用户管理" backTo="/dashboard" />
      <main className="max-w-5xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
            <input className="border rounded px-3 py-2" placeholder="邮箱" value={filters.email} onChange={e=>setFilters({ ...filters, email: e.target.value, page: 1 })} />
            <select className="border rounded px-3 py-2" value={filters.role} onChange={e=>setFilters({ ...filters, role: e.target.value as any, page: 1 })}>
              <option value="">全部角色</option>
              <option value="admin">管理员</option>
              <option value="operator">运营</option>
              <option value="auditor">审核</option>
            </select>
            {user.role === 'system_admin' && (
              <select className="border rounded px-3 py-2" value={manageMerchantId} onChange={e=>{ setManageMerchantId(e.target.value); setFilters({ ...filters, page: 1 }) }}>
                {allMerchants.map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="mb-3 font-semibold">创建用户</div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
            <input className="border rounded px-3 py-2" placeholder="邮箱" value={creating.email} onChange={e=>setCreating({ ...creating, email: e.target.value })} />
            <input className="border rounded px-3 py-2" placeholder="密码" type="password" value={creating.password} onChange={e=>setCreating({ ...creating, password: e.target.value })} />
            <input className="border rounded px-3 py-2" placeholder="公司/组织" value={creating.companyName} onChange={e=>setCreating({ ...creating, companyName: e.target.value })} />
            <select className="border rounded px-3 py-2" value={creating.role} onChange={e=>setCreating({ ...creating, role: e.target.value as any })}>
              <option value="operator">运营</option>
              <option value="auditor">审核</option>
              <option value="admin">管理员</option>
            </select>
            {(user.role === 'system_admin' || user.role === 'admin') && allMerchants.length > 0 && (
              <select className="border rounded px-3 py-2" value={manageMerchantId} onChange={e=>{ setManageMerchantId(e.target.value); setCreating({ ...creating, merchant_id: e.target.value }) }}>
                {allMerchants.map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            )}
          </div>
          <div className="mt-3"><button className="bg-wechat-green text-white px-4 py-2 rounded" onClick={saveNew}>保存</button></div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left">邮箱</th>
                  <th className="px-3 py-2 text-left">角色</th>
                  <th className="px-3 py-2 text-left">公司/组织</th>
                  {user.role === 'system_admin' && (
                    <th className="px-3 py-2 text-left">归属商户</th>
                  )}
                  <th className="px-3 py-2 text-left">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {list.map(u => (
                  <tr key={u.id}>
                    {editId===u.id ? (
                      <>
                        <td className="px-3 py-2"><input className="border rounded px-2 py-1 w-full" value={editForm.email} onChange={e=>setEditForm({ ...editForm, email: e.target.value })} /></td>
                        <td className="px-3 py-2"><select className="border rounded px-2 py-1 w-full" value={editForm.role} onChange={e=>setEditForm({ ...editForm, role: e.target.value as any })}><option value="admin">管理员</option><option value="operator">运营</option><option value="auditor">审核</option></select></td>
                        <td className="px-3 py-2"><input className="border rounded px-2 py-1 w-full" value={editForm.companyName} onChange={e=>setEditForm({ ...editForm, companyName: e.target.value })} /></td>
                        {user.role === 'system_admin' && (
                          <td className="px-3 py-2">
                            <select className="border rounded px-2 py-1 w-full" value={editForm.merchant_id || ''} onChange={e=>setEditForm({ ...editForm, merchant_id: e.target.value })}>
                              {allMerchants.map(m => (
                                <option key={m.id} value={m.id}>{m.name}</option>
                              ))}
                            </select>
                          </td>
                        )}
                        <td className="px-3 py-2 flex gap-2"><button className="bg-wechat-green text-white px-3 py-1 rounded" onClick={()=>saveEdit(u.id)}>保存</button><button className="px-3 py-1 border rounded" onClick={()=>setEditId(null)}>取消</button></td>
                      </>
                    ) : (
                      <>
                        <td className="px-3 py-2">{u.email}</td>
                        <td className="px-3 py-2">{u.role}</td>
                        <td className="px-3 py-2">{u.companyName}</td>
                        {user.role === 'system_admin' && (
                          <td className="px-3 py-2">{(() => { const m = allMerchants.find(mm => mm.id === String(u.merchant_id || '')); return m ? m.name : '-' })()}</td>
                        )}
                        <td className="px-3 py-2 flex gap-3"><button className="text-wechat-green" onClick={()=>{ setEditId(u.id); setEditForm({ email: u.email, role: u.role, companyName: u.companyName || '', password: '', merchant_id: String(u.merchant_id || '') }) }}>编辑</button><button className="text-red-600" onClick={()=>remove(u.id)}>删除</button></td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex items-center gap-2"><button disabled={filters.page<=1} onClick={()=>setFilters({ ...filters, page: filters.page-1 })} className="px-3 py-1 border rounded">上一页</button><span>第 {filters.page} / {pageCount} 页</span><button disabled={filters.page>=pageCount} onClick={()=>setFilters({ ...filters, page: filters.page+1 })} className="px-3 py-1 border rounded">下一页</button></div>
        </div>
      </main>
    </div>
  )
}
